<template>
    <page-controls>
        <template v-slot:left>
            <div class="grow @3xl/controls:max-w-md">
                <vx-filterable :active="active" @toggle="toggleFilterable" data-test="filters">
                    <template v-slot:placeholder>
                        <span class="mr-2">Find</span>
                        <vx-badge
                            v-if="filtersCount > 0"
                            data-test="reset"
                            :borders="false"
                            size="xs"
                            @click.stop="reset"
                        >
                            {{ pluralized('filter', filtersCount, true) }}
                            <font-awesome-icon widthAuto :icon="faCircleXmark" />
                        </vx-badge>
                    </template>
                    <template v-slot:form>
                        <form @submit.prevent="apply" class="flex flex-col gap-4">
                            <div>
                                <filter-label :canReset="!!filters.query" @reset="filters.query = undefined">
                                    Keyword Search
                                </filter-label>
                                <vx-input
                                    type="text"
                                    data-test="query"
                                    placeholder="Search"
                                    maxlength="80"
                                    size="lg"
                                    v-model="filters.query"
                                >
                                    <template v-slot:right>
                                        <font-awesome-icon widthAuto class="text-xs" :icon="faMagnifyingGlass" />
                                    </template>
                                </vx-input>
                            </div>

                            <div v-if="FEATURES.ai_message_vetting">
                                <filter-label
                                    :canReset="filters.ai_message_vetting_agent_id !== undefined"
                                    @reset="
                                        filters.ai_message_vetting_agent_id = undefined;
                                        selectedAgent = undefined;
                                    "
                                >
                                    AI Agent
                                </filter-label>
                                <!-- @vue-ignore -->
                                <vx-selectable
                                    v-model="selectedAgent"
                                    data-test="agent-filter"
                                    placeholder="Search agents"
                                    :clearable="false"
                                    :options="agentOptions"
                                    :loading="agentSearchLoading"
                                    label="name"
                                    size="lg"
                                    @search="searchAgents"
                                />
                            </div>

                            <div class="flex gap-2 pt-4">
                                <vx-button
                                    data-test="cancel"
                                    size="lg"
                                    type="button"
                                    color="muted-light"
                                    @click="cancel"
                                >
                                    Cancel
                                </vx-button>
                                <vx-button data-test="apply" size="lg" type="submit" block>
                                    Apply
                                    <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                                </vx-button>
                            </div>
                        </form>
                    </template>
                </vx-filterable>
            </div>
            <div class="flex w-full gap-2 @xl/controls:w-auto">
                <vx-button-group size="lg" class="w-full">
                    <vx-select
                        v-model="filters.sort_field"
                        @update:modelValue="apply"
                        :options="[
                            {
                                value: 'id',
                                label: 'Sort: Created',
                            },
                            {
                                value: 'name',
                                label: 'Sort: Name',
                            },
                        ]"
                    ></vx-select>
                    <vx-button
                        @click.prevent="
                            filters.sort_dir = filters.sort_dir === 'asc' ? 'desc' : 'asc';
                            apply();
                        "
                        type="button"
                        color="white"
                        shadow
                    >
                        <font-awesome-icon
                            widthAuto
                            :icon="props.modelValue.sort_dir === 'asc' ? faArrowUpWideShort : faArrowDownWideShort"
                        ></font-awesome-icon>
                    </vx-button>
                </vx-button-group>
            </div>
        </template>
        <template v-slot:right>
            <vx-button
                v-if="isAdminOrOwner"
                :disabled="props.list.length >= 20"
                size="lg"
                data-test="new-button"
                :href="`/settings/teams/${teamId}/message-standards/new`"
            >
                New Standards <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
            </vx-button>
        </template>
    </page-controls>
</template>

<script setup lang="ts">
import {
    faArrowDownWideShort,
    faArrowUpWideShort,
    faCircleCheck,
    faMagnifyingGlass,
    faCircleXmark,
    faCirclePlus,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxButtonGroup,
    VxFilterable,
    VxInput,
    VxSelect,
    VxSelectable,
} from '@voxie/frontend-components';
import { computed, nextTick, ref, watch, onMounted, inject } from 'vue';
import PageControls from '~/components/general/PageControls.vue';
import FilterLabel from '~/components/general/FilterLabel.vue';
import { pluralized } from '~/components/filters';
import { useSearchRequest } from '~/composables/useSearchRequest';
import { aiMessageVettingAgentsClient } from '~/services';
import type { Features } from '~/provides/features.types';
import type { AiMessageVettingAgent } from '~/services/aiMessageVettingAgents/aiMessageVettingAgentsClient.types';
import type {
    MessageStandardType,
    GetMessageStandardsParams,
} from '~/services/messageStandards/messageStandardsClient.types';

const teamId = Spark.state.currentTeam.id;
const isAdminOrOwner = Spark.isAdminOrOwner;
const FEATURES = inject('FEATURES') as Features;

const filters = ref<GetMessageStandardsParams>({});

const selectedAgent = ref<AiMessageVettingAgent | undefined>();

const {
    loading: agentSearchLoading,
    response: agentSearchResponse,
    createSearch,
} = useSearchRequest<AiMessageVettingAgent[]>(aiMessageVettingAgentsClient);

const agentOptions = computed(() => agentSearchResponse.value || []);

const searchAgents = createSearch(async (query: string) => {
    const { data } = await aiMessageVettingAgentsClient.getAiMessageVettingAgents({ teamId }, { query });
    return data.data;
});

watch(selectedAgent, (agent) => {
    filters.value.ai_message_vetting_agent_id = agent?.id;
});

const props = defineProps<{
    modelValue: GetMessageStandardsParams;
    list: MessageStandardType[];
    loading?: boolean;
}>();

watch(
    () => props.modelValue,
    (modelValue) => {
        filters.value = { ...modelValue };
        if (modelValue.ai_message_vetting_agent_id === undefined) {
            selectedAgent.value = undefined;
        }
    },
    {
        deep: true,
        immediate: true,
    }
);

onMounted(() => {
    if (FEATURES.ai_message_vetting) {
        searchAgents('');
    }
});

const emit = defineEmits<{
    'update:modelValue': [value: GetMessageStandardsParams];
    apply: [];
    reset: [];
}>();

const active = ref(false);

const toggleFilterable = (event: boolean) => {
    active.value = event;
};

const filtersCount = computed(() => {
    let count = 0;
    if (props.modelValue.query) {
        count++;
    }
    if (props.modelValue.ai_message_vetting_agent_id !== undefined) {
        count++;
    }
    return count;
});

const reset = () => {
    active.value = false;
    emit('reset');
};

const cancel = async () => {
    filters.value = { ...props.modelValue };
    active.value = false;
};

const apply = async () => {
    active.value = false;
    emit('update:modelValue', { ...filters.value });
    await nextTick();
    emit('apply');
};
</script>
